import React, { useEffect, useState } from "react";
import { Box, CircularProgress, SxProps, Theme } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { FulcrumImage } from "../../FulcrumImage";
import { isFulcrumPhotoUrl } from "../../../../utilities/fulcrumLibService";
import { validateCloudFrontCookies } from "../../../../utilities/cloudFrontAuth";
import { enqueueUniqueSnackbar } from "../../../../utilities/snackbarUtils";

interface SafeThumbnailImageProps {
  item: any;
  src: string;
  alt?: string;
  sx?: SxProps<Theme>;
  onError?: () => void;
  onLoad?: () => void;
}

/**
 * Renders a thumbnail with a fallback to the full-resolution image, showing a
 * spinner overlay until the image (or its fallback) finishes loading.
 *
 * Newly-uploaded media may not have a generated `thumbnail.jpg` on the CDN yet,
 * so if the thumbnail fails to load, retry with the full-resolution image
 * (`primary_image.url` / `item.url`) before giving up.
 */
export const SafeThumbnailImage: React.FC<SafeThumbnailImageProps> = ({ item, src, alt = "", sx, onError, onLoad }) => {
  const [useFullImageFallback, setUseFullImageFallback] = useState(false);
  const [loading, setLoading] = useState(true);
  const fullImageUrl = item?.primary_image?.url || item?.url;

  useEffect(() => {
    setUseFullImageFallback(false);
    setLoading(true);
  }, [src]);

  const displaySrc = useFullImageFallback && fullImageUrl ? fullImageUrl : src;

  const handleError = () => {
    if (!useFullImageFallback && fullImageUrl && fullImageUrl !== src) {
      setUseFullImageFallback(true);
      return;
    }
    // Both thumbnail and full-image failed — check if CloudFront cookies are expired
    const cookieValidation = validateCloudFrontCookies();
    if (!cookieValidation.isValid) {
      enqueueUniqueSnackbar(enqueueSnackbar, "CDN access may have expired — try logging out and back in to restore images.", {
        variant: "warning",
        autoHideDuration: 8000
      });
    }
    setLoading(false);
    onError?.();
  };

  const handleLoad = () => {
    setLoading(false);
    onLoad?.();
  };

  return (
    <Box sx={{ ...sx, position: "relative", overflow: "hidden" }}>
      {isFulcrumPhotoUrl(displaySrc) ? (
        <FulcrumImage
          src={displaySrc}
          alt={alt}
          thumbnail={true}
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          onError={handleError}
          onLoad={handleLoad}
        />
      ) : (
        <Box
          component='img'
          src={displaySrc}
          alt={alt}
          sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          onError={handleError}
          onLoad={handleLoad}
        />
      )}
      {loading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#344054"
          }}
        >
          <CircularProgress size={16} sx={{ color: "#98A2B3" }} />
        </Box>
      )}
    </Box>
  );
};
